import { useState, useEffect, useCallback, useRef } from 'react';
import { evaluationService } from '../services/evaluation.service';
import { EvaluationResultData } from '../types';

// In-memory client cache to instantly restore evaluation results on re-render / tab switch
const clientResultsCache = new Map<string, EvaluationResultData>();
const inFlightRequests = new Map<string, Promise<EvaluationResultData>>();

export function useResults(attemptId: string | undefined) {
  const initialCached = attemptId ? clientResultsCache.get(attemptId) || null : null;
  const [data, setData] = useState<EvaluationResultData | null>(initialCached);
  const [loading, setLoading] = useState<boolean>(!initialCached);
  const [error, setError] = useState<string | null>(null);
  const [pollCount, setPollCount] = useState<number>(0);
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const requestSeqRef = useRef(0);
  const isMountedRef = useRef(true);
  const dataRef = useRef<EvaluationResultData | null>(initialCached);
  dataRef.current = data;

  const fetchResults = useCallback(async (forceEval = false) => {
    if (!attemptId) {
      setData(null);
      setLoading(false);
      return;
    }
    const seq = ++requestSeqRef.current;
      if (!dataRef.current && !clientResultsCache.has(attemptId)) {
        setLoading(true);
      }
      setError(null);
      if (forceEval) {
        setPollCount(0);
        clientResultsCache.delete(attemptId);
      }
      try {
        const cacheKey = `${attemptId}:${forceEval}`;
        let fetchPromise = inFlightRequests.get(cacheKey);
        if (!fetchPromise) {
          fetchPromise = evaluationService.getResults(attemptId, forceEval).finally(() => {
            inFlightRequests.delete(cacheKey);
          });
          inFlightRequests.set(cacheKey, fetchPromise);
        }
        const result = await fetchPromise;
        // Ignore stale responses (attempt switched / newer request) and post-unmount updates
        if (!isMountedRef.current || seq !== requestSeqRef.current) return;
        if (result && (result.overallScore !== undefined || (Array.isArray(result.criteria) && result.criteria.length > 0))) {
          clientResultsCache.set(attemptId, result);
        }
        setData(result);
      } catch (err: any) {
        if (!isMountedRef.current || seq !== requestSeqRef.current) return;
        setError(err?.message || 'Failed to load evaluation results');
      } finally {
        if (isMountedRef.current && seq === requestSeqRef.current) {
          setLoading(false);
        }
      }
}, [attemptId]);

useEffect(() => {
  isMountedRef.current = true;
  setPollCount(0);

  const cached = attemptId ? clientResultsCache.get(attemptId) || null : null;
  if (cached) {
    setData(cached);
    setLoading(false);
  } else {
    setData(null);
    setLoading(true);
  }

  fetchResults();

  return () => {
    isMountedRef.current = false;
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
    }
  };
  }, [attemptId, fetchResults]);

const hasEvaluationData = Boolean(
  data?.overallScore !== undefined ||
  (Array.isArray(data?.criteria) && data.criteria.length > 0)
);

const attemptStatus = data?.status || '';

// Gentle polling for in-progress or scoring states (skip if report data is already populated)
useEffect(() => {
  if (!attemptId || !data || hasEvaluationData) return;

  const isPending = ['SCORING', 'EVALUATING', 'SUBMITTING', 'SUBMITTED', 'PROCESSING', 'QUEUED'].includes(attemptStatus);
  const isInProgressPending = attemptStatus === 'IN_PROGRESS' && pollCount < 8;
  if ((isPending && pollCount < 25) || isInProgressPending) {
    // Exponential backoff: starts at 2s, gradually increases to 3.5s to reduce load
    const pollDelay = Math.min(3500, 2000 + pollCount * 250);
    pollTimerRef.current = setTimeout(async () => {
      try {
        // If polling has made 15+ attempts (~40-45s) without completion, request server auto-recovery
        const forceRecovery = isPending && pollCount >= 15;
        const updated = await evaluationService.getResults(attemptId, forceRecovery);
        if (!isMountedRef.current) return;
        if (updated && (updated.overallScore !== undefined || (Array.isArray(updated.criteria) && updated.criteria.length > 0))) {
          clientResultsCache.set(attemptId, updated);
        }
        setData(updated);
        setPollCount((prev) => prev + 1);
      } catch {
        // ignore transient poll error
      }
    }, pollDelay);
  }

  return () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
    }
  };
  }, [attemptId, attemptStatus, hasEvaluationData, pollCount]);

return {
  data,
  loading,
  error,
  isScoring: !hasEvaluationData && ['SCORING', 'EVALUATING', 'SUBMITTING', 'SUBMITTED', 'PROCESSING', 'QUEUED'].includes(data?.status || ''),
  isInProgress: ['INITIALIZING', 'IN_PROGRESS', 'PAUSED'].includes(data?.status || ''),
  isCompleted: (['COMPLETED', 'SCORED', 'EVALUATED'].includes(data?.status || '') || hasEvaluationData) && Boolean(data?.criteria?.length || data?.overallScore !== undefined),
  refresh: (force = true) => fetchResults(force),
};
}
